import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { useUITranslation } from '../../../i18n/index.js';
import { useUserSession } from '@vertesia/ui/session';
import { MonacoEditor, SchemaEditor, useSchema } from '@vertesia/ui/widgets';
import { Button, useToast, useTheme, Panel } from '@vertesia/ui/core';
import { Ajv } from "ajv";
export function ObjectSchemaEditor({ objectType, onSchemaUpdate, readonly = false }) {
    const { store } = useUserSession();
    const { t } = useUITranslation();
    const toast = useToast();
    const { theme } = useTheme();
    const [isUpdating, setUpdating] = useState(false);
    const schema = useSchema(objectType.object_schema);
    const editorRef = useRef(undefined);
    const [displayJson, setDisplayJson] = useState(false);
    const value = useMemo(() => {
        return jsonToContent(schema.schema);
    }, [schema]);
    const onSave = () => {
        if (displayJson) {
            if (!updateSchemaFromJson()) {
                return;
            }
        }
        setUpdating(true);
        store.types.update(objectType.id, { object_schema: schema.schema }).then(() => {
            onSchemaUpdate(schema);
            toast({
                status: 'success',
                title: t('store.schemaUpdated'),
                description: t('store.schemaUpdatedSuccess'),
                duration: 2000
            });
        }).catch((err) => {
            toast({
                status: 'error',
                title: t('store.failedToUpdateSchema'),
                description: err.message,
                duration: 5000
            });
        }).finally(() => {
            setUpdating(false);
        });
    };
    const handleOnSave = () => {
        if (displayJson) {
            if (!updateSchemaFromJson()) {
                return;
            }
        }
        setDisplayJson(prev => !prev);
    };
    const updateSchemaFromJson = () => {
        if (editorRef.current) {
            const value = editorRef.current.getValue();
            try {
                const newSchema = contentToJson(value);
                validateSchema(newSchema);
                schema.replaceSchema(newSchema);
            }
            catch (err) {
                toast({
                    status: 'error',
                    title: t('store.invalidJsonSchema'),
                    description: err.message,
                    duration: 5000
                });
                return false;
            }
        }
        return true;
    };
    const title = (_jsxs("div", { className: 'flex gap-2 items-center', children: [_jsx("div", { className: "text-base font-semibold", children: t('store.schemaEditor') }), !readonly && _jsx("div", { children: _jsx(Button, { variant: "outline", size: "sm", onClick: handleOnSave, children: displayJson ? t('store.editSchema') : t('store.editJson') }) })] }));
    return (_jsx(Panel, { title: title, className: "bg-background! h-full", action: !readonly ? _jsx(Button, { isLoading: isUpdating, variant: "outline", size: "sm", onClick: onSave, children: t('modal.saveChanges') }) : undefined, children: displayJson
            ? _jsx(MonacoEditor, { value: value, language: "json", editorRef: editorRef, theme: theme === 'dark' ? 'vs-dark' : 'vs', options: { readOnly: readonly } })
            : _jsx(SchemaEditor, { schema: schema, readonly: readonly }) }));
}
function jsonToContent(json) {
    if (!json) {
        return '';
    }
    return JSON.stringify(json, null, 2);
}
function contentToJson(content) {
    if (!content?.trim()) {
        return undefined;
    }
    return JSON.parse(content.trim());
}
const validateSchema = (schema) => {
    try {
        const ajv = new Ajv({
            strict: false, // Enable strict mode to ensure all properties are validated
        });
        // Compile the schema. This implicitly validates the schema definition
        // against the JSON Schema draft that ajv supports by default.
        ajv.compile(schema);
    }
    catch (error) {
        throw new Error(`Invalid JSON Schema definition: ${error.message}`, { cause: error });
    }
};
//# sourceMappingURL=ObjectSchemaEditor.js.map